export type ColorMode = "light" | "dark" | "system";

export type ThemePreset = "default" | "mint" | "sand" | "midnight" | "cyberpunk";

export type RadiusPreset = "none" | "sm" | "md" | "lg" | "full";

/**
 * AppTemplate controls the overall visual shell of an application:
 * typography pairing, sidebar style, card density, border weight, and default radius.
 *
 * - clean    → Nunito Sans + Fredoka (soft, modern, friendly)
 * - fintech  → Manrope (dense, geometric precision, tabular figures)
 * - wellness → Nunito Sans + Fredoka (airy, rounded, warm)
 * - bold     → Manrope + Fredoka Bold (strong borders, punchy display)
 * - minimal  → Manrope + Newsreader (hairline clean + literary editorial serif)
 * - forest   → Plus Jakarta Sans / Manrope + Newsreader (dark teal & warm cream luxury)
 */
export type AppTemplate = "clean" | "fintech" | "wellness" | "bold" | "minimal" | "forest";

export interface ThemeConfig {
  colorMode: ColorMode;
  preset: ThemePreset;
  radius: RadiusPreset;
  template: AppTemplate;
  fontSans?: string;
  fontMono?: string;
}

export interface ThemeContextValue {
  theme: ThemeConfig;
  setColorMode: (mode: ColorMode) => void;
  setPreset: (preset: ThemePreset) => void;
  setRadius: (radius: RadiusPreset) => void;
  setTemplate: (template: AppTemplate) => void;
  resolvedColorMode: "light" | "dark";
  presets: { value: ThemePreset; label: string; color: string }[];
  radiusOptions: { value: RadiusPreset; label: string }[];
  templates: { value: AppTemplate; label: string; description: string; fonts?: string }[];
}

export const THEME_PRESETS: { value: ThemePreset; label: string; color: string }[] = [
  { value: "default",   label: "Ocean Blue",  color: "#3b82f6" },
  { value: "mint",      label: "Pastel Mint",  color: "#2dba84" },
  { value: "sand",      label: "Warm Sand",    color: "#d97706" },
  { value: "midnight",  label: "Midnight",     color: "#7c3aed" },
  { value: "cyberpunk", label: "Cyberpunk",    color: "#facc15" },
];

export const RADIUS_OPTIONS: { value: RadiusPreset; label: string }[] = [
  { value: "none", label: "Sharp" },
  { value: "sm",   label: "Subtle" },
  { value: "md",   label: "Default" },
  { value: "lg",   label: "Rounded" },
  { value: "full", label: "Pill" },
];

export const APP_TEMPLATES: { value: AppTemplate; label: string; description: string; fonts: string }[] = [
  { value: "clean",    label: "Clean",    description: "Soft & friendly",          fonts: "Nunito Sans + Fredoka" },
  { value: "fintech",  label: "Fintech",  description: "Dense & precise",          fonts: "Manrope" },
  { value: "wellness", label: "Wellness", description: "Airy & organic",           fonts: "Nunito Sans + Fredoka" },
  { value: "bold",     label: "Bold",     description: "Assertive & high-contrast", fonts: "Manrope + Fredoka Bold" },
  { value: "minimal",  label: "Minimal",  description: "Editorial & clean",        fonts: "Manrope + Newsreader" },
  { value: "forest",   label: "Forest",   description: "Dark teal & warm cream",   fonts: "Plus Jakarta + Newsreader" },
];
